<template>
  <div>
    <div class="ib-title">
      {{ name }}
    </div>
    <div class="ib-price">
      <div class="line">
        <img
          class="icon-up"
          src="/img/capital/price_tag.svg"
          alt="price"
        >
        <span>{{ $t(`auCapitals.investBlock.price${postfix}`) }} &ndash;</span>
        <AuDecimalIcon
          :value="priceBase"
          :img-src="tokenIcon"
          :decimal="mnemonicDecimals"
          :indent="mnemonicIndent"
          :tooltip-text="mnemonicEng"
        />
      </div>
      <div class="line">
        <img
          class="icon-up"
          src="/img/capital/profit.svg"
          alt="Minimum invested size"
        >
        <span>{{ $t("auCapitals.investBlock.minQuote") }} &ndash;</span>
        <span v-if="minQuote && minQuote != 0">
          <AuDecimalIcon
            :value="minQuote"
            :img-src="tokenIcon"
            decimal="0"
            indent="0"
            :tooltip-text="mnemonicEng"
          />
        </span>
        <span v-else>
          <AuDecimalIcon
            :value="priceBase"
            :img-src="tokenIcon"
            :decimal="mnemonicDecimals"
            :indent="mnemonicIndent"
            :tooltip-text="mnemonicEng"
          />
        </span>
      </div>
      <div class="line">
        <div class="d-flex gap-1 align-items-center">
          <img
            class="blockchain-img"
            :src="logoIcon"
            alt="logo"
          >
          <span><a
            :href="scannerTokenLink"
            target="_blank"
          >{{ tokenBase?.mnemonicEng }}</a></span>
        </div>
      </div>
    </div>
    <div class="ib-info">
      <div
        v-if="minExpertVotes > 0"
        class="record"
      >
        <span>{{ $t("auCapitals.investBlock.expertVotes") }}</span>
        <div class="sep" />
        <span>{{ expertVotes }}&nbsp;&#47;&nbsp;{{ minExpertVotes }}</span>
      </div>
      <div
        v-if="isNotRwa"
        class="record"
      >
        <span>{{ $t("auCapitals.investBlock.contractAdr") }}</span>
        <div class="sep" />
        <span
          v-if="smartContract"
          class="green link"
          @click="goToAdrScanner"
        >{{ shortAddressLowercase(smartContract) }}</span>
        <span
          v-else
          class="grey"
        >{{ $t("auCapitals.investBlock.none") }}</span>
      </div>
      <div
        v-if="isNotRwa"
        class="record"
      >
        <span>{{ $t("auCapitals.investBlock.tokenAdr") }}</span>
        <div class="sep" />
        <span
          v-if="createdAddress"
          class="green link"
          @click="goToTokenScanner"
        >{{ shortAddressLowercase(createdAddress) }}</span>
        <span
          v-else
          class="grey"
        >{{ $t("auCapitals.investBlock.none") }}</span>
      </div>
      <div class="record">
        <span>{{ $t(`auCapitals.investBlock.tokenType${postfix}`) }}</span>
        <div class="sep" />
        <span>{{ assetType }}</span>
      </div>
      <div
        v-if="isAutentic"
        class="record"
      >
        <div class="whitespace-nowrap">
          {{ $t("auCapitals.investBlock.distributionFrequencyLabel") }}
        </div>
        <div class="sep" />
        <div class="text">
          {{ $t("auCapitals.investBlock.distributionFrequencyValue") }}
          <UTooltip :tooltip=" $t('auCapitals.investBlock.distributionFrequencyTootlip', {defaultCurrency})" />
        </div>
      </div>
      <div class="record">
        <div>
          {{ $t("auCapitals.investBlock.expectedIncome") }}
        </div>
        <div class="sep" />
        <div class="text">
          {{ profitability }}%
        </div>
      </div>
      <div class="record">
        <div>
          {{ $t("auCapitals.investBlock.systemComission") }}
        </div>
        <div class="sep" />
        <span v-if="feePercent">
          {{ feePercent + "%" }}
        </span>
        <span v-else>
          {{ $t("auCapitals.investBlock.comissionData") }}
        </span>
      </div>
      <div class="record">
        <div>
          {{ $t("auCapitals.investBlock.userLevel") }}
        </div>
        <div class="sep" />
        <span class="up-letter">{{ userLimitStatuses }}</span>
      </div>
      <div
        v-if="status!=='Open sale'"
        class="record"
      >
        <span class="green">{{ timeText }}</span>
        <div class="sep sep-green" />
        <span class="green">{{ timeDate }}</span>
      </div>
      <div
        v-else
        class="record"
      >
        <span>{{ $t("auCapitals.investBlock.available") }}</span>
        <div class="sep" />
        <span>{{ openSaleLimit + " " + tokenBase?.mnemonicEng }}</span>
      </div>
    </div>
    <div class="ib-progress">
      <div
        :style="infoStyle"
        class="under-info"
      >
        <div
          v-if="minCap && isNotRwa"
          :style="softStyle"
          class="info-wrapper"
        >
          <span class="label">
            {{ $t("auCapitals.projectCard.softCap") }}
          </span>
          <span class="value">
            <AuDecimalStatic
              :value="minCap"
              :addition="mnemonicEng"
              :decimal="mnemonicDecimals"
              :indent="mnemonicIndent"
            />
          </span>
        </div>

        <div
          v-if="status !== 'Open sale'"
          class="info-wrapper"
          :class="{'hard': !minCap}"
        >
          <span class="label">
            {{ $t(`auCapitals.projectCard.hardCap${vote}${postfix}`) }}
          </span>
          <span class="value">
            <AuDecimalStatic
              :value="maxCap"
              :addition="mnemonicEng"
              :decimal="mnemonicDecimals"
              :indent="mnemonicIndent"
            />
          </span>
        </div>
      </div>
      <ProgressBar
        v-if="status !== 'Open sale'"
        class="project-progressbar"
        :procent="progressProcent"
        :green="green"
      />
      <div
        v-if="status !== 'Open sale'"
        class="above-info"
      >
        <div
          :style="amountStyle"
          class="info-wrapper"
        >
          <span class="label">
            {{ $t(`auCapitals.projectCard.amountRaised${vote}`) }}
          </span>
          <span
            id="for-test-value-currentCap"
            class="value for-test"
          >
            <AuDecimalStatic
              :value="currentCap"
              :addition="mnemonicEng"
              :decimal="mnemonicDecimals"
              :indent="mnemonicIndent"
            />
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="showInvestCountBlock"
      class="ib-popular"
    >
      <AuButton
        center
        padding-horizontal="0"
        @click="investCount=investValue[0]"
      >
        <AuDecimalIcon
          :value="investValue[0]"
          :img-src="tokenIcon"
          decimal="0"
          indent="0"
        />
      </AuButton>
      <AuButton
        center
        padding-horizontal="0"
        @click="investCount=investValue[1]"
      >
        <AuDecimalIcon
          :value="investValue[1]"
          :img-src="tokenIcon"
          decimal="0"
          indent="0"
        />
      </AuButton>
      <AuButton
        center
        padding-horizontal="0"
        @click="investCount=investValue[2]"
      >
        <AuDecimalIcon
          :value="investValue[2]"
          :img-src="tokenIcon"
          decimal="0"
          indent="0"
        />
      </AuButton>
    </div>
    <AuInput
      v-if="showInvestCountBlock"
      v-model="investCount"
      :placeholder="$t(`auCapitals.investBlock.investValuePlaceholder${postfix}`)"
    />
    <AuButton
      :class="{'mt-1': !showInvestCountBlock, 'mt-4': showInvestCountBlock}"
      type="primary"
      height="61px"
      width="100%"
      center
      :disabled="isButtonDisabled"
      @click="$emit('invest', investCount)"
    >
      <div class="ib-invest">
        <img
          :src="tokenIcon"
          :class="{ 'img_brightness': isTokenIcon && !isButtonDisabled, 'img_disabled': isButtonDisabled }"
          alt="token"
        >
        <span>{{ $t(`auCapitals.investBlock.buttonTxt${postfix}`) }}</span>
      </div>
    </AuButton>
    <span
      v-if="accessDeniedReasonsText == $t('access.reasons.kycDenied', {platform})"
      class="error mt-2 kyc_error"
    >
      <RouterLink
        to="/kyc"
      >
        <span class="underline">
          {{ $t('access.reasons.errorKycDenied1') }}
        </span>
      </RouterLink>
      <span>
        {{ $t('access.reasons.errorKycDenied2', {platform}) }}
      </span>
    </span>
    <span
      v-else
      class="error mt-2"
    >
      {{ accessDeniedReasonsText }}
    </span>
  </div>
</template>

<script>
import _ from "lodash";
import BigNumber from "bignumber.js";
import moment from "moment";
import { mapGetters } from "vuex";

import ProgressBar from "@/components/progressBar/ProgressBar";
import UTooltip from "@/components/uikit/uComponents/UTooltip.vue";
import { checkAccess, checkPostfix, getAccessDeniedReasonsText, scope } from "@/help/accessData";
import { isUserLevel } from "@/help/systemSettings";

export default {
  name: "InvestBlock",
  components: { ProgressBar, UTooltip },
  props: {
    name: {
      type: String,
      default: ""
    },
    priceBase: {
      type: String,
      default: ""
    },
    mnemonicDecimals: {
      type: String,
      default: ""
    },
    mnemonicIndent: {
      type: String,
      default: ""
    },
    mnemonicEng: {
      type: String,
      default: ""
    },
    minCount: {
      type: String,
      default: ""
    },
    minCap: {
      type: String,
      default: ""
    },
    maxCap: {
      type: String,
      default: ""
    },
    createdAddress: {
      type: String,
      default: ""
    },
    feePercent: {
      type: String,
      default: ""
    },
    smartContract: {
      type: String,
      default: ""
    },
    expertVotes: {
      type: Number,
      default: 0
    },
    minExpertVotes: {
      type: Number,
      default: 0
    },
    hasEscrow: {
      type: Boolean,
      default: false
    },
    terms: {
      type: String,
      default: ""
    },
    assetType: {
      type: String,
      default: ""
    },
    profitability: {
      type: String,
      default: ""
    },
    currentCap: {
      type: String,
      default: ""
    },
    progressProcent: {
      type: String,
      default: ""
    },
    status: {
      type: String,
      default: ""
    },
    timer: {
      type: String,
      default: ""
    },
    dateClose: {
      type: String,
      default: ""
    },
    dateEnd: {
      type: String,
      default: ""
    },
    projectType: {
      type: String,
      default: ""
    },
    rawStart: {
      type: String,
      default: ""
    },
    rawEnd: {
      type: String,
      default: ""
    },
    allowedForUsers: {
      type: String,
      default: undefined
    },
    tokenBase: {
      type: String,
      default: () => {}
    },
    tokenQuote: {
      type: Object,
      default: () => {}
    },
    userLimitStatuses: {
      type: String,
      default: "base"
    },
    openSaleLimit: {
      type: String,
      default: "0"
    }
  },
  data() {
    return {
      investCount: null
    };
  },
  computed: {
    ...mapGetters({
      language: "language",
      profile: "profile/profile",
      apiSystemSettings: "apiSystemSettings"
    }),

    defaultCurrency() {
      return process.env.VUE_APP_CURRENCY_DEFAULT;
    },

    minQuote() {
      const price = BigNumber(this.priceBase).dividedBy(BigNumber(10).pow(this.mnemonicDecimals));
      const value = BigNumber(this.minCount).multipliedBy(price);
      return value.isNaN() ? "0" : value.toString(10);
    },

    tokenIcon() {
      return this.tokenQuote?.logoMain ?? "/svg/companies/s_logo.svg";
    },

    isTokenIcon() {
      return this.tokenQuote?.logoMain;
    },

    logoIcon() {
      return this.tokenBase?.blockChain ? this.tokenBase?.blockChain.logotype :
        (this.tokenBase?.logoMain ?? this.tokenBase?.mainImage?.name) ?? "/svg/companies/s_logo.svg";
    },

    softStyle() {
      const percent = this.getPercentage(this.minCap, this.maxCap);

      let stype = {
        "margin-left": (percent > 70 ? 70 : percent) + "%",
        "text-align": percent > 70 ? "end" : "start"
      };

      if (percent <= 70) {
        stype["border-left"] = "2px rgba(40, 229, 147, 0.2509803922) solid";
        stype["padding-left"] = "5px";
      }
      else {
        stype["border-right"] = "2px rgba(40, 229, 147, 0.2509803922) solid";
        stype["padding-right"] = "5px";
        stype["margin-right"] = "5px";
      }

      return stype;
    },

    infoStyle() {
      const percent = this.getPercentage(this.minCap, this.maxCap);
      if (percent > 70) {
        return {
          "justify-content": "end"
        };
      }
      return {
      };
    },

    amountStyle() {
      const percent = this.getPercentage(this.currentCap, this.maxCap);

      let stype = {
        "margin-left": (percent > 85 ? 0 : percent) + "%",
        "text-align": percent > 85 ? "end" : "start"
      };

      if (percent > 85) {
        stype.width = "100%";
      }

      return stype;
    },

    timeText() {
      return this.status === "Waiting" ? this.$t("auCapitals.investBlock.start") :
        this.$t("auCapitals.investBlock.end");
    },

    timeDate() {
      if (!this.rawEnd) {
        return this.$t("projects.none");
      }
      const diff = moment(this.status === "Waiting" ? this.rawStart : this.rawEnd).diff(moment(), "seconds");

      if (this.status == "Completed") {
        return this.$t("auCapitals.investBlock.closed");
      }
      if (diff < 1 && this.status == "Waiting") {
        return " 0 seconds";
      }
      if (diff < 1 && this.status != "Waiting") {
        return this.$t("auCapitals.investBlock.closed");
      }

      if (diff <= 3600) {
        if (this.timer == " -1 seconds") {
          return "";
        }
        return this.timer;
      }

      if (diff > 3600 && diff <= (24 * 60 * 60)) {
        const hour = Math.floor(diff / (60 * 60));
        const min = Math.floor(diff / 60);
        if (hour > 0) {
          return hour + " " + this.$t("auCapitals.investBlock.hour");
        }
        return min + " " + this.$t("auCapitals.investBlock.min");
      }
      else {
        const days = Math.floor(diff / (24 * 60 * 60));
        return days + " " + this.$t("auCapitals.investBlock.day");
      }
    },

    investValue() {
      const delta = BigNumber(this.maxCap).minus(this.currentCap).dividedBy(BigNumber(10).pow(this.mnemonicDecimals));
      if (delta.lte(100)) {
        return [];
      }
      if (delta.lte(200)) {
        return ["50", "100", delta.toString(10)];
      }
      if (delta.lte(10000)) {
        return ["100",
          delta.dividedBy(200).integerValue(BigNumber.ROUND_DOWN).multipliedBy(100).toString(10),
          delta.toString(10)];
      }

      const sharePrice = BigNumber(this.priceBase).dividedBy(BigNumber(10).pow(this.mnemonicDecimals));
      const a = sharePrice * 1;
      const b = sharePrice * 3;
      const c = sharePrice * 5;

      return [a, b, c];
    },

    isAllowed() {
      if (this.isIssuer) {
        return false;
      }
      if (!this.allowedForUsers) {
        return true;
      }
      return _.some(this.profile.roles, role => role == this.allowedForUsers);
    },

    hasAccessToDeposit() {
      return checkAccess(scope.deposit);
    },

    postfix() {
      return checkPostfix();
    },

    isNotRwa() {
      return checkAccess(scope.notRwa);
    },

    platform() {
      return process.env.VUE_APP_PLATFORM;
    },

    accessDeniedReasonsText() {
      const textParams = { platform: process.env.VUE_APP_PLATFORM };
      return getAccessDeniedReasonsText(scope.deposit, textParams);
    },

    isAutentic() {
      return process.env.VUE_APP_PLATFORM == "Autentic";
    },

    isIssuer() {
      if (this.$auth.user()) {
        return this.$auth.user().type == "issuer";
      }
      return false;
    },

    showInvestCountBlock() {
      return this.investValue.length > 0 && this.status === "Open";
    },

    isVoting() {
      return this.projectType === "voting";
    },

    vote() {
      return this.isVoting ? "Vote" : "";
    },

    goToContractScanner() {
      return `${process.env.VUE_APP_SCAN_BASE_URL}contract/${this.smartContract}`;
    },

    scannerTokenLink() {
      return `${process.env.VUE_APP_SCAN_BASE_URL}token/${this.createdAddress}`;
    },

    isButtonDisabled() {
      if (this.status === "Open sale" && this.isAllowed && this.hasAccessToDeposit) {
        return false;
      }
      return this.status !== "Open" || !this.isAllowed || this.progressProcent >= 100 || !isUserLevel() || !this.hasAccessToDeposit;
    },

    green() {
      return this.status === "Waiting" || this.status === "Open";
    }
  },
  methods: {
    shortAddressLowercase(address) {
      return this.$filters.shortAddress(address).toLowerCase();
    },

    goToAdrScanner() {
      window.open(this.goToContractScanner, "_blank");
    },

    goToTokenScanner() {
      window.open(this.scannerTokenLink, "_blank");
    },

    getPercentage(dividend, divider) {
      const percentage = BigNumber(dividend).dividedBy(divider).multipliedBy(100);
      if (percentage.isGreaterThanOrEqualTo(100)) {
        return 100;
      }

      return Math.round(percentage.toNumber());
    }
  }
};
</script>

<style scoped lang="scss">
.ib-title {
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 25px;
  line-height: 1.25em;
}
.ib-price {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 25px;

  a {
    text-decoration: underline;
    color: var(--text-color);
  }

  .blockchain-img {
    border-radius: 50%;
    height: 24px;
    width: 24px;
  }

  .token-icon {
    width: 30px;
    height: 30px;
  }

  .value {
    position: relative;
    right: -2px;
  }

  .icon-up {
    position: relative;
    top: -2px;
  }

  .line {
    display: flex;
    align-items: center;
    gap: 3px;
  }
}
.ib-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 25px;

  .record {
    display: flex;
    align-items: baseline;
    gap: 10px;

   .green {
     color: #279063;
   }
   .link {
     cursor: pointer
   }

   .text {
    text-align: end;
   }

    .sep {
      height: 2px;
      flex: 1;
      border-bottom: 1px dashed var(--color-50);
      margin-bottom: 4px;
      opacity: 0.5;
    }
    .up-letter {
      text-transform: capitalize;
    }

    .sep-green {
      border-bottom: 1px dashed #279063;
    }
  }
}
.ib-progress {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 25px;
  .under-info {
    display: flex;
    justify-content: space-between;
  }
  .info-wrapper {
    display: flex;
    flex-direction: column;
    width: fit-content;
    .label {
      font-size: 12px;
      color: var(--color-50);
    }
    .value {
      font-size: 15px;
      color: var(--text-color);
    }
  }
  .hard {
    width: 100%;
    align-items: end;
  }
}
.ib-popular {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  margin-bottom: 15px;
}
.ib-invest {
  display: flex;
  align-items: center;
  gap: 10px;
  span {
    font-size: 22px;
  }
  img {
    width: 30px;
    height: 30px;
  }
  .img_brightness {
    filter: brightness(0) invert(1);
  }
  .img_disabled {
    filter: brightness(0) invert(1);
  }
}
.grey {
  color: var(--color-50);
}
.kyc_error {
  display: inline;
}
.underline {
  color: red;
  text-decoration: underline;
}
</style>